import React, {useEffect, useMemo, useState} from 'react';
import {Box, Typography} from '@mui/material';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {t} from 'i18next';
import {Trans} from 'react-i18next';
import {useSelector} from 'react-redux';
import './BatchRedactionTree.scss';
import {ReactComponent as InfoIcon} from '../../../assets/images/info-icon.svg';
import ArrTreeView from '../../common/arr-tree-view/ArrTreeView';
import {
  useAnalyzeSearch,
  useGetAnalysisResult,
  useGetSearchTreeData,
} from '../../../hooks/redactions/useBatchRedaction';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../shared/interfaces';
import {ToastType} from '../../../shared/constants';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {
  formatRedactionTreeData,
  getAllIdsForNode,
} from '../../../utils/formatRedactionTreeData';
import ViewReportModal from '../view-report-modal/ViewReportModal';
import {IApplyRedactionData} from '../../../api/types/redactions.type';
import {RootState} from '../../../redux/store';

interface IBatchRedactionTreeProps {
  onRedactionCompletion: () => void;
}

const BatchRedactionTree: React.FC<IBatchRedactionTreeProps> = ({
  onRedactionCompletion,
}) => {
  const singalrLastEvent = useSelector(
    (state: RootState) => state.signalr.lastEvent
  );

  const {
    data: treeDataResponse,
    isLoading,
    refetch: getSearchtree,
  } = useGetSearchTreeData();
  const {mutate: getAnalysisResult, isPending: isAnalysisPending} =
    useGetAnalysisResult();
  const {mutate: analyzeBatchRedaction, isPending: isSearchPending} =
    useAnalyzeSearch();

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState(ToastType.Success);
  const [applyRedactionData, setApplyRedactionData] = useState<
    IApplyRedactionData[]
  >([]);
  const [showApplyRedactionModal, setShowApplyRedactionModal] = useState(false);
  const [ids, setIds] = useState<string[]>([]);
  const [previousSelectedRedactionTag, setPreviousSelectedRedactionTag] =
    useState<any>(null);

  useEffect(() => {
    if (
      singalrLastEvent &&
      singalrLastEvent.payload?.type === 'BatchRedaction'
    ) {
      getSearchtree();
    }
  }, [singalrLastEvent, getSearchtree]);

  const treeData = useMemo(() => {
    if (treeDataResponse && treeDataResponse?.length > 0) {
      return formatRedactionTreeData(treeDataResponse);
    }
    return undefined;
  }, [treeDataResponse]);

  const handleActionClick = (node: TreeViewBaseItem<ExtendedTreeItemProps>) => {
    const allIds: string[] = node.children
      ? getAllIdsForNode(node.children, node.id)
      : [];
    setIds(allIds);
    if (node.actionButtonName === 'View Report & Apply Redactions') {
      getAnalysisData(allIds);
    } else if (node.isGroup) {
      batchRedaction(allIds);
      batchRedaction([node.id]);
    } else {
      batchRedaction([node.id]);
    }
  };

  const getAnalysisData = (ids: string[]) => {
    getAnalysisResult(ids, {
      onSuccess: (e) => {
        setApplyRedactionData(e);
        setShowApplyRedactionModal(true);
      },
      onError: () => triggerToast(ToastType.Error, t('common.error')),
    });
  };

  const batchRedaction = (ids: string[]) => {
    analyzeBatchRedaction(ids, {
      onSuccess: (success) => {
        triggerToast(ToastType.Success, success.message);
        getSearchtree();
      },
      onError: (error) => {
        triggerToast(ToastType.Error, error.message);
      },
    });
  };

  const triggerToast = (
    toastType: ToastType | ((prevState: ToastType) => ToastType),
    toastMessage: string
  ) => {
    setShowToast(true);
    setToastType(toastType);
    setToastMessage(toastMessage);
  };

  useEffect(() => {
    const liveRegionId = 'batch-redaction-tree-toast-live-region';
    const container = document.querySelector(
      '.batch-redactions-tree-container'
    );
    if (!container) return;
    let liveRegion = document.getElementById(liveRegionId);
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = liveRegionId;
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      Object.assign(liveRegion.style, {
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: '0',
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0 0 0 0)',
        whiteSpace: 'nowrap',
        border: '0',
      });
      container.prepend(liveRegion);
    }
  }, []);
  useEffect(() => {
    const liveRegion = document.getElementById(
      'batch-redaction-tree-toast-live-region'
    );
    if (!liveRegion) return;
    liveRegion.textContent = '';
    const applyDismissButtonAccessibility = () => {
      const dismissButton = Array.from(
        document.querySelectorAll<HTMLButtonElement>(
          '.toast-notification__container button'
        )
      ).find((button) => {
        const text = button.textContent?.replace(/\s+/g, ' ').trim();
        return text === '×' || !!button.querySelector('svg');
      });

      if (
        dismissButton &&
        dismissButton.getAttribute('aria-label') !== 'Dismiss notification'
      ) {
        dismissButton.setAttribute('aria-label', 'Dismiss notification');
      }
    };
    if (!showToast || !toastMessage) {
      applyDismissButtonAccessibility();
      return;
    }
    const timer = window.setTimeout(() => {
      liveRegion.textContent = toastMessage;
      applyDismissButtonAccessibility();
    }, 50);
    const observer = new MutationObserver(() => {
      applyDismissButtonAccessibility();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
      liveRegion.textContent = '';
    };
  }, [showToast, toastMessage]);

  useEffect(() => {
    const isVisible = (element?: Element | null): element is HTMLElement => {
      return !!element && (element as HTMLElement).offsetParent !== null;
    };

    const setAttributeIfChanged = (
      element: Element,
      name: string,
      value: string
    ) => {
      if (element.getAttribute(name) !== value) {
        element.setAttribute(name, value);
      }
    };

    const applyAccessibility = () => {
      const container = document.querySelector(
        '.batch-redactions-tree-container'
      );

      if (!container) return;

      const progressBars = container.querySelectorAll('[role="progressbar"]');

      progressBars.forEach((progressBar) => {
        const progressBarElement = progressBar as HTMLElement;

        if (document.activeElement !== progressBarElement) {
          setAttributeIfChanged(progressBarElement, 'tabindex', '-1');
        }
      });

      const treeItems = Array.from(
        container.querySelectorAll('[role="treeitem"]')
      ).filter((treeItem) => {
        const element = treeItem as HTMLElement;
        return element.offsetParent !== null;
      });

      treeItems.forEach((treeItem) => {
        const treeItemElement = treeItem as HTMLElement;
        const rowText = treeItemElement.textContent
          ?.replace(/\s+/g, ' ')
          .trim();

        if (rowText) {
          setAttributeIfChanged(treeItemElement, 'aria-label', rowText);
        }

        const buttons = Array.from(
          treeItemElement.querySelectorAll('button')
        ).filter((button) => (button as HTMLElement).offsetParent !== null);

        buttons.forEach((button) => {
          const buttonElement = button as HTMLButtonElement;
          const buttonText = buttonElement.textContent
            ?.replace(/\s+/g, ' ')
            .trim();
          const expanded = buttonElement.getAttribute('aria-expanded');

          if (expanded !== null) {
            setAttributeIfChanged(
              buttonElement,
              'aria-label',
              `${expanded === 'true' ? 'Collapse' : 'Expand'}${
                rowText ? ` ${rowText}` : ''
              }`
            );
            return;
          }

          if (buttonText) {
            setAttributeIfChanged(
              buttonElement,
              'aria-label',
              `${buttonText}${rowText ? ` ${rowText}` : ''}`
            );
          }
        });

        const infoIcons = Array.from(
          treeItemElement.querySelectorAll('svg, [data-testid="info-icon"]')
        ).filter((icon) => {
          const element = icon as HTMLElement;
          return (
            element.offsetParent !== null &&
            !element.classList.contains('info-icon-small')
          );
        });

        infoIcons.forEach((infoIcon) => {
          const infoIconElement = infoIcon as HTMLElement;

          if (!infoIconElement.getAttribute('aria-label')) {
            infoIconElement.setAttribute('aria-label', t('common.infoTooltip'));
          }

          if (!infoIconElement.hasAttribute('tabindex')) {
            infoIconElement.setAttribute('tabindex', '0');
          }
        });
      });

      if (!showApplyRedactionModal) return;

      const modalTitle = t('batchRedactions.modal.title');
      const modal = Array.from(
        document.querySelectorAll<HTMLElement>('[role="dialog"]')
      ).find((dialog) => {
        const ariaLabel = dialog.getAttribute('aria-label')?.trim();
        return isVisible(dialog) && ariaLabel === modalTitle;
      });

      if (!modal) return;

      const redactionTagInfoIcon = modal.querySelector<HTMLElement>(
        '.dropdown-container [data-testid="info-icon"]'
      );

      const redactionTagDropdown = modal.querySelector<HTMLElement>(
        [
          '[data-testid="select-redaction-tag-dropdown"] [role="combobox"]',
          '[data-testid="select-redaction-tag-dropdown"] button',
          '[data-testid="select-redaction-tag-dropdown"] input',
          '[data-testid="select-redaction-tag-dropdown"]',
        ].join(', ')
      );

      const downloadButton =
        modal.querySelector<HTMLElement>('#download-button') ??
        modal.querySelector<HTMLElement>('[data-testid="download-button"]');

      const footerCloseButton = modal.querySelector<HTMLElement>(
        'button#cancel-button'
      );

      const redactButton = modal.querySelector<HTMLElement>(
        'button#status-button'
      );

      if (redactionTagInfoIcon) {
        setAttributeIfChanged(
          redactionTagInfoIcon,
          'aria-label',
          t('common.infoTooltip')
        );

        if (!redactionTagInfoIcon.hasAttribute('tabindex')) {
          redactionTagInfoIcon.setAttribute('tabindex', '0');
        }
      }

      if (redactionTagDropdown) {
        setAttributeIfChanged(
          redactionTagDropdown,
          'aria-label',
          t('batchRedactions.modal.selectRedactionTag')
        );
      }

      if (downloadButton) {
        setAttributeIfChanged(
          downloadButton,
          'aria-label',
          t('batchRedactions.modal.downloadPreRedactionReport')
        );
      }

      if (footerCloseButton) {
        setAttributeIfChanged(
          footerCloseButton,
          'aria-label',
          t('common.close')
        );
      }

      if (redactButton) {
        setAttributeIfChanged(
          redactButton,
          'aria-label',
          t('batchRedactions.modal.redact')
        );
      }
    };

    const timer = window.requestAnimationFrame(applyAccessibility);
    const observer = new MutationObserver(() => {
      applyAccessibility();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      window.cancelAnimationFrame(timer);
      observer.disconnect();
    };
  }, [
    treeData,
    isAnalysisPending,
    isSearchPending,
    isLoading,
    showApplyRedactionModal,
  ]);

  return (
    <Box className="batch-redactions-tree-container">
      <Box className="tree-title">
        <Typography
          className="typo-text subhead5_size"
          data-testid={`batch-redaction-tree-header`}
          tabIndex={0}
        >
          {t('batchRedactions.tree.header')}
        </Typography>
        <ArrTooltipWrapper
          title={t('batchRedactions.tree.tooltipTitle')}
          trigger={
            <InfoIcon
              className="info-icon info-icon-small"
              data-testid="info-icon"
              tabIndex={0}
              aria-label={t('common.infoTooltip')}
            />
          }
          closeTooltipOnEscPress
        >
          <Trans
            i18nKey={t('batchRedactions.tree.tooltipContent')}
            components={{
              br: <br data-testid={`batch-redaction-tree-br`} />,
              h3: (
                <Typography
                  component="h3"
                  data-testid={`batch-redaction-tree-typography`}
                />
              ),
            }}
          ></Trans>
        </ArrTooltipWrapper>
      </Box>
      <Box className="tree-body">
        {isLoading ? (
          <ArrLoadingSpinner />
        ) : (
          treeData && (
            <ArrTreeView
              showActions={true}
              itemsList={treeData}
              multipleSelection={false}
              isShowCountLabel={false}
              isShowCheckbox={false}
              customContentHeight={42}
              showButtonLoader={isAnalysisPending || isSearchPending}
              onButtonClick={handleActionClick}
              buttonTestId="arr-view-report-button"
              stopActionButtonClickPropagation={true}
              stopActionButtonKeyPropagation={true}
            />
          )
        )}
      </Box>
      {showApplyRedactionModal && (
        <ViewReportModal
          idList={ids}
          analysisResultDetails={applyRedactionData}
          showReportModal={showApplyRedactionModal}
          onConfirm={() => {
            getSearchtree();
            onRedactionCompletion?.();
            setShowApplyRedactionModal(false);
          }}
          onCancel={() => setShowApplyRedactionModal(false)}
          triggerToast={(status: ToastType, message: string) =>
            triggerToast(status, message)
          }
          onRedactionTagChange={setPreviousSelectedRedactionTag}
          initialSelectedRedactionTag={previousSelectedRedactionTag}
        />
      )}
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          onClose={() => setShowToast(false)}
          status={[toastType]}
        />
      )}
    </Box>
  );
};

export default BatchRedactionTree;
